<template>
  <b-row>
    <!-- Profile image (not uploadable during add) -->
    <b-col md="3" class="mb-3 text-center">
      <div class="member-avatar-placeholder mx-auto">
        <i class="fa fa-user"></i>
      </div>
      <b-alert show variant="info" class="mt-3 small text-left">
        <i class="fa fa-info-circle mr-1"></i>
        สามารถแก้ไขรูปโปรไฟล์ได้หลังจากเพิ่มผู้เรียนสำเร็จ
      </b-alert>
    </b-col>

    <b-col md="9">
      <b-tabs content-class="mt-3">
        <b-tab title="ข้อมูลผู้เรียน" active>
          <h5 class="card-title mb-3">ข้อมูลผู้เรียน</h5>

          <div class="form-row">
            <b-form-group class="col-md-6">
              <label>ชื่อ (ภาษาไทย) <span class="text-danger">*</span></label>
              <b-form-input
                v-model="form.firstName"
                type="text"
                placeholder="ชื่อ (ภาษาไทย)"
                :class="{ 'is-invalid': hasError('firstName') }"
                trim
                :formatter="thaiFormatter"
                @keypress="onThaiKeyPress"
                @input="sanitizeThaiInput('firstName')"
              ></b-form-input>
              <b-form-invalid-feedback>กรุณากรอกชื่อ</b-form-invalid-feedback>
            </b-form-group>
            <b-form-group class="col-md-6">
              <label>นามสกุล (ภาษาไทย) <span class="text-danger">*</span></label>
              <b-form-input
                v-model="form.lastName"
                type="text"
                placeholder="นามสกุล (ภาษาไทย)"
                :class="{ 'is-invalid': hasError('lastName') }"
                trim
                :formatter="thaiFormatter"
                @keypress="onThaiKeyPress"
                @input="sanitizeThaiInput('lastName')"
              ></b-form-input>
              <b-form-invalid-feedback
                >กรุณากรอกนามสกุล</b-form-invalid-feedback
              >
            </b-form-group>
          </div>

          <div class="form-row">
            <b-form-group class="col-md-6">
              <label>ชื่อ (ภาษาอังกฤษ) <span class="text-danger">*</span></label>
              <b-form-input
                v-model="form.firstNameEn"
                type="text"
                placeholder="ชื่อ (ภาษาอังกฤษ)"
                :class="{ 'is-invalid': hasError('firstNameEn') }"
                trim
                :formatter="englishFormatter"
                @keypress="onEnglishKeyPress"
                @input="sanitizeEnglishInput('firstNameEn')"
              ></b-form-input>
              <b-form-invalid-feedback
                >กรุณากรอกชื่อ (ภาษาอังกฤษ)</b-form-invalid-feedback
              >
            </b-form-group>
            <b-form-group class="col-md-6">
              <label>นามสกุล (ภาษาอังกฤษ) <span class="text-danger">*</span></label>
              <b-form-input
                v-model="form.lastNameEn"
                type="text"
                placeholder="นามสกุล (ภาษาอังกฤษ)"
                :class="{ 'is-invalid': hasError('lastNameEn') }"
                trim
                :formatter="englishFormatter"
                @keypress="onEnglishKeyPress"
                @input="sanitizeEnglishInput('lastNameEn')"
              ></b-form-input>
              <b-form-invalid-feedback
                >กรุณากรอกนามสกุล (ภาษาอังกฤษ)</b-form-invalid-feedback
              >
            </b-form-group>
          </div>

          <div class="form-row">
            <b-form-group class="col-md-6">
              <label>ชื่อเล่น</label>
              <b-form-input
                v-model="form.nickName"
                type="text"
                placeholder="ชื่อเล่น"
                trim
              ></b-form-input>
            </b-form-group>
            <b-form-group class="col-md-6">
              <label>วันเดือนปีเกิด (ค.ศ 4 หลัก)</label>
              <b-form-input
                v-model="form.dateOfBirth"
                type="date"
                :max="today"
              ></b-form-input>
            </b-form-group>
          </div>

          <div class="form-row">
            <b-form-group class="col-md-12">
              <label>เลขประจำตัวประชาชน</label>
              <b-form-input
                v-model="form.idCard"
                type="text"
                placeholder="เลข 13 หลัก"
                :class="{ 'is-invalid': hasError('idCard') }"
                trim
              ></b-form-input>
              <b-form-invalid-feedback>
                กรุณากรอกรหัสบัตรประชาชนให้ถูกต้อง
              </b-form-invalid-feedback>
            </b-form-group>
          </div>

          <div class="form-row">
            <b-form-group class="col-md-6">
              <label>เพศ</label>
              <b-form-select v-model="form.gender" :options="genderOptions">
                <template #first>
                  <b-form-select-option :value="''" disabled
                    >เลือกเพศ</b-form-select-option
                  >
                </template>
              </b-form-select>
            </b-form-group>
            <b-form-group class="col-md-6">
              <label>ระดับชั้น</label>
              <b-form-select
                v-model="form.gradeLevel"
                :options="gradeLevelOptions"
              >
                <template #first>
                  <b-form-select-option :value="''" disabled
                    >เลือกระดับชั้น</b-form-select-option
                  >
                </template>
              </b-form-select>
            </b-form-group>
          </div>

          <div class="form-row">
            <b-form-group class="col-md-12">
              <label>โรงเรียน <span class="text-danger">*</span></label>
              <SchoolSelect
                v-model="form.education"
                :invalid="hasError('education')"
              />
              <div v-if="hasError('education')" class="invalid-feedback d-block">
                กรุณาเลือกโรงเรียน
              </div>
            </b-form-group>
          </div>

          <div class="form-row">
            <b-form-group class="col-md-6">
              <label>เบอร์โทรศัพท์ <span class="text-danger">*</span></label>
              <b-form-input
                v-model="form.phoneNumber"
                type="text"
                placeholder="0999999999"
                maxlength="15"
                :class="{ 'is-invalid': hasError('phoneNumber') }"
                trim
                @keypress="onNumberKeyPress"
                @input="form.phoneNumber = form.phoneNumber.replace(/[^\d]/g, '')"
              ></b-form-input>
              <b-form-invalid-feedback>
                กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group class="col-md-6">
              <label>เบอร์โทรผู้ปกครอง (ไม่บังคับ)</label>
              <b-form-input
                v-model="form.parentPhone"
                type="text"
                placeholder="0999999999"
                maxlength="15"
                :class="{ 'is-invalid': hasError('parentPhone') }"
                trim
                @keypress="onNumberKeyPress"
                @input="form.parentPhone = form.parentPhone.replace(/[^\d]/g, '')"
              ></b-form-input>
              <b-form-invalid-feedback>
                กรุณากรอกเบอร์โทรศัพท์ให้ถูกต้อง
              </b-form-invalid-feedback>
            </b-form-group>
          </div>

          <b-form-group
            class="mb-3"
            label="เกี่ยวกับผู้เรียน"
            label-for="textarea-formatter-wannasorn"
          >
            <b-form-textarea
              id="textarea-formatter-wannasorn"
              v-model="form.about"
              placeholder="เกี่ยวกับผู้เรียน"
            ></b-form-textarea>
          </b-form-group>

          <hr />

          <h5 class="card-title mb-3">ข้อมูลผู้ใช้งาน</h5>
          <div class="form-row">
            <b-col md="6">
              <label>อีเมล <span class="text-danger">*</span></label>
              <b-form-input
                v-model="form.email"
                :class="{ 'is-invalid': hasError('email') }"
                type="email"
                placeholder="example@email.com"
                trim
                @change="checkEmail"
              ></b-form-input>
              <b-form-invalid-feedback
                >กรุณากรอกอีเมลให้ถูกต้อง หรือ
                มีอีเมลนี้ในระบบแล้ว</b-form-invalid-feedback
              >
            </b-col>
            <b-form-group class="col-md-6">
              <label>ชื่อผู้ใช้งาน <span class="text-danger">*</span></label>
              <b-form-input
                v-model="form.username"
                :class="{ 'is-invalid': hasError('username') }"
                type="text"
                placeholder="username"
                trim
                @change="checkUsername"
              ></b-form-input>
              <b-form-invalid-feedback>
                <template v-if="!form.username">กรุณากรอกชื่อผู้ใช้งาน</template>
                <template v-else
                  >ชื่อผู้ใช้งานไม่ถูกต้อง หรือ
                  มีผู้ใช้งานนี้ในระบบอยู่แล้ว</template
                >
              </b-form-invalid-feedback>
            </b-form-group>
          </div>

          <change-password-form
            :companyId="companyId"
            :errors="errors"
            :isSetPassword="isSetPassword"
            :isSetConfirmPassword="isSetConfirmPassword"
            @passwordConfirmed="onPasswordConfirmed"
            :required="true"
            @passwordCleared="$emit('passwordCleared', $event)"
          ></change-password-form>
        </b-tab>

        <b-tab title="รูปสำหรับยืนยันตัวตน">
          <b-alert show variant="info" class="mt-2">
            <i class="fa fa-info-circle mr-1"></i>
            สามารถเพิ่มรูปสำหรับยืนยันตัวตนได้หลังจากเพิ่มผู้เรียนสำเร็จ
          </b-alert>
        </b-tab>
      </b-tabs>
    </b-col>
  </b-row>
</template>

<script>
// Extended learner form used when adding a member for a Wannasorn company. The create-time sibling
// of WannasornMemberEditForm.vue (which is the inline PATCH-per-field edit form). The parent owns
// the `form` object, validation and submit; this component only renders the inputs.
import ChangePasswordForm from "./ChangePasswordForm.vue";
import SchoolSelect from "./SchoolSelect.vue";
import { gradeLevelListByCompany } from "@/helpers/grade-level";
import { genderList } from "@/helpers/gender";

export default {
  components: { ChangePasswordForm, SchoolSelect },
  props: {
    form: { type: Object, required: true },
    errors: { type: Array, default: () => [] },
    companyId: { type: [Number, String], default: null },
    isSetPassword: { type: Boolean, default: null },
    isSetConfirmPassword: { type: Boolean, default: null },
  },
  data() {
    return {
      genderOptions: genderList(),
    };
  },
  computed: {
    today() {
      return new Date().toISOString().split("T")[0];
    },
    gradeLevelOptions() {
      return gradeLevelListByCompany(this.companyId);
    },
  },
  methods: {
    hasError(field) {
      return this.errors.includes(field);
    },
    checkUsername(username) {
      this.$emit("checkUsername", username);
    },
    checkEmail(email) {
      this.$emit("checkEmail", email);
    },
    onPasswordConfirmed(data) {
      this.$emit("passwordConfirmed", data);
    },
    onThaiKeyPress(event) {
      const char = String.fromCharCode(event.which || event.keyCode);
      if (!/^[฀-๿\s]$/.test(char)) {
        event.preventDefault();
      }
    },
    onEnglishKeyPress(event) {
      const char = String.fromCharCode(event.which || event.keyCode);
      if (!/^[A-Za-z\s]$/.test(char)) {
        event.preventDefault();
      }
    },
    onNumberKeyPress(event) {
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault();
      }
    },
    sanitizeThaiInput(field) {
      this.form[field] = this.form[field].replace(/[^฀-๿\s]/g, '');
    },
    sanitizeEnglishInput(field) {
      this.form[field] = this.form[field].replace(/[^A-Za-z\s]/g, '');
    // Formatters also strip characters that arrive by paste, which keypress guards miss.
    },
    thaiFormatter(value) {
      return value.replace(/[^฀-๿\s]/g, '');
    },
    englishFormatter(value) {
      return value.replace(/[^A-Za-z\s]/g, '');
    },
  },
};
</script>

<style scoped>
.member-avatar-placeholder {
  width: 160px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e9ecef;
  border-radius: 4px;
  color: #adb5bd;
}
.member-avatar-placeholder i {
  font-size: 5rem;
}
</style>
